import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router-dom";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Icon from "@material-ui/core/Icon";

import { useAuth } from "context/AuthContext";
import api from "api/index";

const NAVY        = "#1a237e";
const NAVY_DARK   = "#0d1b6e";
const NAVY_LIGHT  = "#1565c0";

const styles = {
  page: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: `linear-gradient(150deg, ${NAVY_DARK} 0%, ${NAVY} 50%, ${NAVY_LIGHT} 100%)`,
    padding: "24px",
    fontFamily: "'Roboto', sans-serif",
    position: "relative",
    overflow: "hidden"
  },

  /* subtle geometric accent circles */
  circle1: {
    position: "absolute",
    width: "480px",
    height: "480px",
    borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.06)",
    top: "-120px",
    right: "-140px",
    pointerEvents: "none"
  },
  circle2: {
    position: "absolute",
    width: "320px",
    height: "320px",
    borderRadius: "50%",
    border: "1px solid rgba(255,255,255,0.05)",
    bottom: "-80px",
    left: "-80px",
    pointerEvents: "none"
  },

  /* card */
  card: {
    width: "100%",
    maxWidth: "420px",
    background: "#ffffff",
    borderRadius: "20px",
    boxShadow: "0 32px 80px rgba(0,0,0,0.32), 0 8px 24px rgba(0,0,0,0.18)",
    overflow: "hidden",
    position: "relative",
    zIndex: 1
  },

  /* navy top bar */
  topBar: {
    background: `linear-gradient(90deg, ${NAVY_DARK}, ${NAVY})`,
    padding: "28px 36px 24px",
    display: "flex",
    alignItems: "flex-start",
    gap: "16px"
  },
  crest: {
    width: "52px",
    height: "52px",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.15)",
    border: "2px solid rgba(255,255,255,0.3)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    fontSize: "16px",
    color: "#fff",
    flexShrink: 0,
    letterSpacing: "-0.5px"
  },
  schoolName: {
    margin: 0,
    color: "#ffffff",
    fontSize: "17px",
    fontWeight: 700,
    lineHeight: 1.25
  },
  schoolSub: {
    margin: "4px 0 0",
    color: "rgba(255,255,255,0.6)",
    fontSize: "11.5px",
    lineHeight: 1.4
  },
  schoolTag: {
    display: "inline-block",
    marginTop: "8px",
    background: "rgba(255,255,255,0.12)",
    border: "1px solid rgba(255,255,255,0.2)",
    borderRadius: "20px",
    padding: "3px 10px",
    fontSize: "10px",
    fontWeight: 700,
    color: "rgba(255,255,255,0.8)",
    textTransform: "uppercase",
    letterSpacing: "0.8px"
  },

  /* form body */
  body: {
    padding: "28px 36px 32px"
  },
  heading: {
    margin: "0 0 6px",
    fontSize: "18px",
    fontWeight: 700,
    color: "#0f172a"
  },
  subHeading: {
    margin: "0 0 24px",
    fontSize: "13px",
    color: "#94a3b8"
  },

  /* error */
  errorBox: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    background: "#fef2f2",
    border: "1px solid #fecaca",
    borderRadius: "8px",
    padding: "10px 14px",
    marginBottom: "18px",
    color: "#b91c1c",
    fontSize: "13px"
  },

  /* fields */
  fieldGroup: { marginBottom: "18px" },
  label: {
    display: "block",
    fontSize: "11px",
    fontWeight: 700,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.6px",
    marginBottom: "7px"
  },
  inputWrap: { position: "relative", display: "flex", alignItems: "center" },
  input: {
    width: "100%",
    height: "46px",
    padding: "0 46px 0 14px",
    border: "1.5px solid #e2e8f0",
    borderRadius: "10px",
    fontSize: "14px",
    color: "#0f172a",
    background: "#f8fafc",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "inherit",
    transition: "border-color 150ms, box-shadow 150ms, background 150ms"
  },
  inputIcon: {
    position: "absolute",
    right: "14px",
    color: "#cbd5e1",
    display: "flex",
    alignItems: "center",
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer"
  },

  /* options row */
  optRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    margin: "4px 0 22px"
  },
  rememberLabel: {
    display: "flex",
    alignItems: "center",
    gap: "7px",
    fontSize: "12.5px",
    color: "#475569",
    cursor: "pointer",
    userSelect: "none"
  },
  checkbox: { accentColor: NAVY, cursor: "pointer" },
  forgotBtn: {
    fontSize: "12.5px",
    color: NAVY,
    fontWeight: 600,
    background: "none",
    border: "none",
    padding: 0,
    cursor: "pointer",
    fontFamily: "inherit"
  },

  /* submit */
  submitBtn: {
    width: "100%",
    height: "48px",
    border: "none",
    borderRadius: "10px",
    background: `linear-gradient(90deg, ${NAVY_DARK} 0%, ${NAVY} 100%)`,
    color: "#fff",
    fontSize: "14px",
    fontWeight: 700,
    letterSpacing: "1px",
    textTransform: "uppercase",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontFamily: "inherit",
    transition: "opacity 180ms",
    boxShadow: `0 4px 16px rgba(26,35,126,0.35)`
  },

  /* footer */
  footer: {
    textAlign: "center",
    padding: "0 36px 24px",
    fontSize: "11px",
    color: "#cbd5e1"
  }
};

class LoginPageInner extends Component {
  state = { email: "", password: "", showPassword: false, rememberMe: false, loading: false, error: "" };

  componentDidMount() {
    if (this.props.auth.user) this.props.history.push("/bwillis/dashboard");
  }

  handleSubmit = async e => {
    e.preventDefault();
    const { email, password } = this.state;
    if (!email || !password) { this.setState({ error: "Email and password are required." }); return; }
    this.setState({ loading: true, error: "" });
    try {
      const res = await api.post("/auth/login", { email, password });
      const { token, user } = res.data.data || res.data;
      if (user.role === "Student") {
        this.setState({ error: "This is a student account. Please use the student portal to sign in.", loading: false });
        return;
      }
      this.props.auth.login(token, user);
      this.props.history.push("/bwillis/dashboard");
    } catch (err) {
      this.setState({ error: err.response?.data?.message || "Invalid credentials. Please try again.", loading: false });
    }
  };

  onFocus = e => { e.target.style.borderColor = NAVY; e.target.style.boxShadow = `0 0 0 3px rgba(26,35,126,0.1)`; e.target.style.background = "#fff"; };
  onBlur  = e => { e.target.style.borderColor = "#e2e8f0"; e.target.style.boxShadow = "none"; e.target.style.background = "#f8fafc"; };

  render() {
    const { classes } = this.props;
    const { email, password, showPassword, rememberMe, loading, error } = this.state;

    return (
      <div className={classes.page}>
        <div className={classes.circle1} />
        <div className={classes.circle2} />

        <div className={classes.card}>

          {/* Navy top bar — school identity */}
          <div className={classes.topBar}>
            <div className={classes.crest}>BW</div>
            <div>
              <p className={classes.schoolName}>Bishop Willis Core PTC</p>
              <p className={classes.schoolSub}>Iganga, Uganda</p>
              <span className={classes.schoolTag}>Academic Portal</span>
            </div>
          </div>

          {/* Form */}
          <div className={classes.body}>
            <h2 className={classes.heading}>Sign in</h2>
            <p className={classes.subHeading}>Use your institutional credentials to continue.</p>

            {error && (
              <div className={classes.errorBox}>
                <Icon style={{ fontSize: "16px" }}>error_outline</Icon>
                {error}
              </div>
            )}

            <form onSubmit={this.handleSubmit} noValidate>
              <div className={classes.fieldGroup}>
                <label className={classes.label} htmlFor="login-email">Email Address</label>
                <div className={classes.inputWrap}>
                  <input
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    className={classes.input}
                    value={email}
                    onChange={e => this.setState({ email: e.target.value, error: "" })}
                    onFocus={this.onFocus}
                    onBlur={this.onBlur}
                    placeholder="you@bishopwillis.ac.ug"
                    disabled={loading}
                  />
                  <span className={classes.inputIcon} style={{ cursor: "default" }}>
                    <Icon style={{ fontSize: "18px" }}>mail_outline</Icon>
                  </span>
                </div>
              </div>

              <div className={classes.fieldGroup}>
                <label className={classes.label} htmlFor="login-password">Password</label>
                <div className={classes.inputWrap}>
                  <input
                    id="login-password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    className={classes.input}
                    value={password}
                    onChange={e => this.setState({ password: e.target.value, error: "" })}
                    onFocus={this.onFocus}
                    onBlur={this.onBlur}
                    placeholder="••••••••••"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className={classes.inputIcon}
                    onClick={() => this.setState(s => ({ showPassword: !s.showPassword }))}
                    tabIndex={-1}
                  >
                    <Icon style={{ fontSize: "18px" }}>{showPassword ? "visibility_off" : "visibility"}</Icon>
                  </button>
                </div>
              </div>

              <div className={classes.optRow}>
                <label className={classes.rememberLabel}>
                  <input
                    type="checkbox"
                    className={classes.checkbox}
                    checked={rememberMe}
                    onChange={e => this.setState({ rememberMe: e.target.checked })}
                  />
                  Remember me
                </label>
                <button type="button" className={classes.forgotBtn}>Forgot password?</button>
              </div>

              <button
                type="submit"
                className={classes.submitBtn}
                disabled={loading}
                style={loading ? { opacity: 0.7, cursor: "not-allowed" } : {}}
              >
                {loading
                  ? <CircularProgress size={18} style={{ color: "#fff" }} />
                  : <><Icon style={{ fontSize: "18px" }}>login</Icon> Sign In</>}
              </button>
            </form>
          </div>

          <div className={classes.footer}>
            &copy; {new Date().getFullYear()} Bishop Willis Core PTC &mdash; All rights reserved
          </div>
        </div>
      </div>
    );
  }
}

LoginPageInner.propTypes = {
  classes: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  auth: PropTypes.object.isRequired
};

const LoginPageStyled = withStyles(styles)(LoginPageInner);

function LoginPage(props) {
  const auth = useAuth();
  return <LoginPageStyled {...props} auth={auth} />;
}

export default withRouter(LoginPage);
